import { useQueries } from '@tanstack/react-query';
import { invokeAction } from 'actions';
import { useAuthStatus } from 'features/auth';
import { unitStatusOptionsOf, } from './types';
/** Backend max page size — the popup shows one page per status group. */
const UNITS_PAGE_SIZE = 100;
/**
 * One query per status the action advertises, in backend enum order. Statuses are
 * queried individually (rather than fetching everything and grouping client-side)
 * because the endpoint filters by a single `leadEngagementStatus` per call.
 */
export function useProjectUnitGroups({ action, actionName, projectKey, projectIdURI, enabled }) {
    const authStatus = useAuthStatus();
    const user = authStatus.kind === 'authenticated' ? authStatus.user : null;
    const statuses = action ? unitStatusOptionsOf(action) : [];
    const results = useQueries({
        queries: statuses.map((status) => ({
            queryKey: ['projectUnits', user?.email, projectKey, actionName, status.id],
            enabled: enabled && Boolean(user && action && projectIdURI),
            queryFn: async () => {
                if (!user || !action)
                    throw new Error(`${actionName} unavailable`);
                const response = await invokeAction(action, user.token, {
                    variables: {
                        object: projectIdURI,
                        pageSize: String(UNITS_PAGE_SIZE),
                        currentPage: '1',
                        // `''` is a real filter value (units with no engagement yet), so it is
                        // sent as an empty param rather than omitted.
                        leadEngagementStatus: status.id,
                    },
                });
                if (!response.ok)
                    throw new Error(`${actionName} returned ${response.status}`);
                const json = (await response.json());
                const view = json.filteredView;
                return { units: view?.item ?? [], total: view?.numberOfItems ?? 0 };
            },
        })),
    });
    const groups = statuses.map((status, index) => {
        const result = results[index];
        return {
            status,
            units: result?.data?.units ?? [],
            total: result?.data?.total ?? 0,
            isPending: result?.isPending ?? true,
            isError: result?.isError ?? false,
        };
    });
    return { groups, isPending: results.some((result) => result.isPending) };
}
